import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Bell, Check, CheckCheck, X } from 'lucide-react';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchNotifs = async () => {
    try {
      const { data } = await api.get('/notifications/inbox');
      setNotifications(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch {}
  };

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/mark-read/${id}`);
    fetchNotifs();
  };

  const markAllRead = async () => {
    await api.post('/notifications/mark-all-read');
    fetchNotifs();
  };

  const severityColor = (s) => {
    if (s === 'critical') return '#D4726A';
    if (s === 'warning') return '#BA7517';
    return '#2A7B88';
  };

  const typeIcon = (type) => {
    if (type === 'overdue') return 'clock';
    if (type === 'revenue_low') return 'trending-down';
    if (type === 'contract_expiring') return 'file-warning';
    return 'bell';
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1.5 rounded-md hover:bg-gray-100 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell size={18} style={{ color: '#6B7280' }} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1"
            style={{ backgroundColor: '#D4726A' }}
            data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-lg border shadow-lg z-50 overflow-hidden"
          style={{ borderColor: '#E5E1DC' }} data-testid="notification-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}>
            <span className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
              Notifications {unread > 0 && `(${unread})`}
            </span>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded hover:bg-gray-200"
                  style={{ color: '#2A7B88' }} data-testid="mark-all-read-btn">
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)}><X size={14} style={{ color: '#9CA3AF' }} /></button>
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell size={24} style={{ color: '#E5E1DC' }} className="mx-auto mb-2" />
                <p className="text-sm" style={{ color: '#9CA3AF' }}>All clear!</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div key={n.id} className="flex items-start gap-3 px-4 py-3 border-b hover:bg-gray-50 transition-colors"
                  style={{ borderColor: '#F3F4F6' }} data-testid={`notif-${n.id}`}>
                  <div className="mt-0.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: severityColor(n.severity) }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs leading-relaxed" style={{ color: '#1B2A4A' }}>{n.message}</p>
                    {n.created_at && (
                      <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>
                        {new Date(n.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                  <button onClick={() => markRead(n.id)} className="flex-shrink-0 p-0.5 rounded hover:bg-gray-200" title="Dismiss">
                    <Check size={12} style={{ color: '#9CA3AF' }} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
